import React, { useEffect, useRef, useState } from "react";
import "./PostAssets.scss";
import dayjs from "dayjs";
import { PropertyApi } from "../../../../../utils/api/Property";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import { CommonEnums } from "../../../../../enums/common.enums";
import CustomPageLoader from "../../../../../components/loaders/CustomPageLoader";
import { useParams, useLocation } from "react-router-dom";
import PostAssetMenu from "./PostAssetMenu";
import PreviewModal from "../../propertypost/view/PreviewModal";
import Upload from "../../../../../components/icons/Upload";

interface ImageData {
  url: string;
  relativePath: string;
}

interface TemplateData {
  filename: string;
  downloadUrl: string;
  relativePath: string;
}

const PostAssets: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mainImage, setMainImage] = useState<ImageData | null>(null);
  const [mainTemplate, setMainTemplate] = useState<TemplateData | null>(null);
  const { onboardingPostId } = useParams<{ onboardingPostId: string }>();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImageUrls, setPreviewImageUrls] = useState<string[]>([]);

  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [updatedImageFile, setUpdatedImageFile] = useState<File | null>(null);
  const [updatedImagePreviewUrl, setUpdatedImagePreviewUrl] = useState<
    string | null
  >(null);
  const queryParams = new URLSearchParams(location.search);
  const postType = queryParams.get("post_type");

  const fetchPropertyPost = async () => {
    if (!onboardingPostId) return;
    try {
      setLoading(true);
      const res = await PropertyApi.GetOnboardingPostById(onboardingPostId);

      if (res?.data?.success) {
        const post = res.data.data;

        let designActivityRef: any = null;
        if (postType === "story_post") {
          designActivityRef = post.story_post_design_activity_ref;
        } else if (postType === "video_post") {
          designActivityRef = post.video_post_design_activity_ref;
        } else if (postType === "reel_post") {
          designActivityRef = post.reel_post_design_activity_ref;
        } else {
          designActivityRef = post.image_post_design_activity_ref;
        }

        const designTask = designActivityRef?.design_task_id;

        if (designTask) {
          const imageUrl = designTask.output_image_path
            ? CommonHelpers.getImageUrlFromImageTool({
                path: designTask.output_image_path,
              })
            : null;

          setMainImage(
            imageUrl
              ? {
                  url: imageUrl,
                  relativePath: designTask.output_image_path,
                }
              : null
          );

          const templateUrl = designTask.output_image_template_path
            ? CommonHelpers.getImageUrlFromImageTool({
                path: designTask.output_image_template_path,
              })
            : null;

          setMainTemplate(
            templateUrl
              ? {
                  filename: designTask.output_image_template_path
                    .split("/")
                    .pop()!,
                  downloadUrl: templateUrl,
                  relativePath: designTask.output_image_template_path,
                }
              : null
          );
        }
      } else {
        setError("Failed to load property post");
      }
    } catch (err) {
      setError("Something went wrong while fetching");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (onboardingPostId) fetchPropertyPost();
  }, [onboardingPostId, postType]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUpdatedImageFile(file);
      setUpdatedImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpdateImage = async () => {
    if (!updatedImageFile) return;

    console.log("Uploading file:", updatedImageFile);

    alert("Image updated successfully! (Simulation)");
    setUpdatedImageFile(null);
    setUpdatedImagePreviewUrl(null);
    setIsUpdateMode(false);
    fetchPropertyPost();
  };

  const handleCancelUpdate = () => {
    setUpdatedImageFile(null);
    setUpdatedImagePreviewUrl(null);
  };

  useEffect(() => {
    return () => {
      if (updatedImagePreviewUrl) {
        URL.revokeObjectURL(updatedImagePreviewUrl);
      }
    };
  }, [updatedImagePreviewUrl]);

  const renderDesignImage = (item: any) => {
    const designActivityRef = item?._id ? item : null;

    const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
    const designTaskStatus = designActivityRef?.design_task_id?.task_status;

    const isLoading = designTaskStatus === CommonEnums.taskStatus.queued;
    const isErrored = designTaskStatus === CommonEnums.taskStatus.errored;

    const assetUrl = assetUrlRaw
      ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
      : null;

    if (isErrored) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error Preview"
            className="activity-thumbnail placeholder-image"
          />
        </div>
      );
    }

    if (isLoading) {
      const fallback =
        postType === CommonEnums.postType.video_post.key
          ? "/images/square-fallback-image-for-video-preview.png"
          : "/images/square-fallback-image-for-image-preview.png";

      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src={fallback}
            alt="Loading Preview"
            className="activity-thumbnail placeholder-image"
          />
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    if (postType === "story_post") {
      return (
        <div className="activity-image-section story-post-image-container">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Story Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === "video_post") {
      return (
        <div className="activity-image-section video-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Video Preview"
            className="activity-thumbnail"
          />
        </div>
      );
    }

    if (postType === "reel_post") {
      return (
        <div className="activity-image-section reel-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Reel Preview"
            className="activity-thumbnail"
          />
        </div>
      );
    }

    return (
      <div className="activity-image-section">
        <img
          src={
            assetUrl ||
            "/images/image-video-generation-error-fallback-image-for-card.png"
          }
          alt="Image Preview"
          className="activity-thumbnail"
        />
      </div>
    );
  };

  if (loading)
    return (
      <div className="post-assets-loader">
        <CustomPageLoader />
      </div>
    );

  if (error)
    return (
      <div className="post-assets-error">
        <p>{error}</p>
      </div>
    );

  const handlePreview = (imageUrl: string) => {
    setPreviewImageUrls([imageUrl]);
    setIsPreviewOpen(true);
  };

  return (
    <div className="post-assets-container">
      <div className="section">
        <h2 className="section-title">Output Images For the Post</h2>
        <p className="section-subtitle">
          Post images generated from the design automation tool
        </p>

        {mainImage && (
          <div className="main-image-section">
            {isUpdateMode ? (
              <div className="update-image-container">
                <div className="update-header-wrapper">
                  <h3 className="sub-heading">Main Image</h3>
                  <button
                    className="cancel-update-btn"
                    onClick={() => {
                      setIsUpdateMode(false);
                      handleCancelUpdate();
                    }}
                  >
                    Cancel
                  </button>
                </div>

                {updatedImagePreviewUrl ? (
                  <div className="image-preview-container">
                    <img
                      src={updatedImagePreviewUrl}
                      alt="New preview"
                      className="preview-image"
                    />
                    <div className="preview-actions"></div>
                  </div>
                ) : (
                  <>
                    <div
                      className="file-uploader-box"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload />
                      <p className="uploader-text">Drop or Select file</p>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                      accept="image/*"
                    />
                  </>
                )}
              </div>
            ) : (
              <>
                <h3 className="sub-heading">Main Image</h3>
                <div className="review-image-container-with-menu">
                  {renderDesignImage({
                    _id: "temp",
                    design_task_id: {
                      output_image_path: mainImage.relativePath,
                    },
                  })}
                  <div className="activity-options-menu">
                    <PostAssetMenu
                      previewImageUrl={mainImage.url}
                      onPreview={() => handlePreview(mainImage.url)}
                      onUpdate={() => setIsUpdateMode(true)}
                    />
                  </div>
                </div>
                <p className="relative-path-title">
                  Relative File path on Design Tool
                </p>
                <p className="relative-path">{mainImage.relativePath}</p>
              </>
            )}
          </div>
        )}
      </div>

      {mainTemplate && (
        <div className="section">
          <h2 className="section-title">Output Design Templates</h2>
          <p className="section-subtitle">
            Design template source file generated from the design automation
            tool for the post designs
          </p>
          <div className="template-block">
            <div className="template-header">
              <h3 className="template-sub-heading">Main Template</h3>
              <a
                href={mainTemplate.downloadUrl}
                className="download-btn"
                download
              >
                Download
              </a>
            </div>
            <div className="template-filename">{mainTemplate.filename}</div>
          </div>
          <p className="design-path-title">Relative File path on Design Tool</p>
          <p className="design-path">{mainTemplate.relativePath}</p>
        </div>
      )}

      <PreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        images={previewImageUrls}
      />
    </div>
  );
};

export default PostAssets;
